// Schema del flow: settings, flow completo, request/response API.
import { z } from 'zod';
import { PersonaSchema, BoundariesSchema } from './persona.js';
import { FlowQuestionSchema } from './flow-question.js';
import { FlowOutcomeSchema } from './flow-outcome.js';
// Campi identità raccoglibili nel form pre-chat
export const IdentityFieldEnum = z.enum([
    'first_name',
    'last_name',
    'date_of_birth',
    'phone',
    'email',
]);
export const AskPersonalInfoSchema = z.object({
    enabled: z.boolean().default(true),
    fields: z.array(IdentityFieldEnum).default(['first_name', 'last_name']),
    // Quando raccogliere l'identità nel flusso
    timing: z.enum(['at_start', 'at_end', 'skip']).default('at_start'),
    required: z.boolean().default(false),
});
export const EmergencyExitSchema = z.object({
    enabled: z.boolean().default(true),
    keywords: z
        .array(z.string().min(2).max(100))
        .default(['dolore al petto', 'difficoltà respiratoria grave', 'perdita coscienza']),
    contact_text: z.string().max(300).default('Se è una situazione urgente, chiama il 112.'),
});
export const FlowSettingsSchema = z.object({
    ask_personal_info: AskPersonalInfoSchema.default({
        enabled: true,
        fields: ['first_name', 'last_name'],
        timing: 'at_start',
        required: false,
    }),
    use_name_in_conversation: z.boolean().default(true),
    formality: z.enum(['tu', 'lei', 'auto']).default('auto'),
    max_turns: z.number().int().min(10).max(100).default(50),
    idle_timeout_minutes: z.number().int().min(1).max(60).default(15),
    allow_skip: z.boolean().default(true),
    emergency_exit: EmergencyExitSchema.default({
        enabled: true,
        keywords: ['dolore al petto', 'difficoltà respiratoria grave', 'perdita coscienza'],
        contact_text: 'Se è una situazione urgente, chiama il 112.',
    }),
});
// Sprint 6: posizioni e archi del flow builder visuale React Flow.
// Le posizioni sono persistite quando l'admin sposta nodi; gli archi
// rappresentano il flow naturale + eventuali skip_if.
export const VisualLayoutNodeSchema = z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    position: z.object({ x: z.number(), y: z.number() }),
    // Dati liberi del nodo (es. questionId, outcomeId). Tipizzati lato UI.
    data: z.record(z.string(), z.unknown()).optional(),
});
export const VisualLayoutEdgeSchema = z.object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
    sourceHandle: z.string().nullable().optional(),
    targetHandle: z.string().nullable().optional(),
    label: z.string().optional(),
    // 'flow' = arco sequenziale, 'skip' = arco skip_if condizionale.
    data: z.record(z.string(), z.unknown()).optional(),
});
export const VisualLayoutSchema = z.object({
    nodes: z.array(VisualLayoutNodeSchema).default([]),
    edges: z.array(VisualLayoutEdgeSchema).default([]),
});
// Flow completo (riga DB + relazioni serializzate)
export const FlowSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    slug: z
        .string()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9-]+$/, 'Solo lettere minuscole, cifre e trattini'),
    language_default: z.string().min(2).max(8).default('it'),
    persona: PersonaSchema,
    boundaries: BoundariesSchema,
    settings: FlowSettingsSchema,
    is_active: z.boolean(),
    visual_layout: VisualLayoutSchema.default({ nodes: [], edges: [] }),
    archived_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    created_by_admin_id: z.string().uuid().nullable(),
});
// Flow + relazioni (per API GET dettaglio)
export const FlowWithRelationsSchema = FlowSchema.extend({
    questions: z.array(FlowQuestionSchema),
    outcomes: z.array(FlowOutcomeSchema),
});
// Payload create/update flow (senza id/timestamps)
export const FlowCreateSchema = z.object({
    name: z.string().min(1).max(120),
    slug: z
        .string()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9-]+$/),
    language_default: z.string().min(2).max(8).default('it'),
    persona: PersonaSchema,
    boundaries: BoundariesSchema.default([]),
    settings: FlowSettingsSchema,
});
export const FlowUpdateSchema = FlowCreateSchema.partial();
// Risposta /api/flow/active (per chat) — formato compatto
export const PublicFlowSchema = z.object({
    id: z.string().uuid(),
    version_id: z.string().uuid(),
    version_number: z.number().int(),
    name: z.string(),
    slug: z.string(),
    language_default: z.string(),
    persona: PersonaSchema,
    settings: FlowSettingsSchema,
    questions: z.array(FlowQuestionSchema),
    outcomes: z.array(FlowOutcomeSchema),
});
//# sourceMappingURL=flow.js.map